import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Upload,
  Download,
  FileJson,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ClipboardCopy,
  Eye,
  Loader2,
  HelpCircle,
  RefreshCw,
  History,
  ExternalLink,
  Info,
  ArrowRight
} from "lucide-react";

const BulkImportExport = ({ passages = [], onImportComplete, onNavigateToPassages }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // Import state
  const [jsonInput, setJsonInput] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  
  // Import settings
  const [importMode, setImportMode] = useState("continue_on_error");
  const [defaultStatus, setDefaultStatus] = useState("draft");
  const [defaultDifficulty, setDefaultDifficulty] = useState("2");
  const [overrideStatus, setOverrideStatus] = useState(false);
  const [overrideDifficulty, setOverrideDifficulty] = useState(false);
  
  // Template state
  const [showTemplate, setShowTemplate] = useState(false);
  const [sampleTemplate, setSampleTemplate] = useState(null);
  
  // Export state
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  
  // History state
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch import history
  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/import-history?limit=10`, {
        headers,
        withCredentials: true
      });
      setImportHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch import history:", error);
    }
  };

  useEffect(() => {
    fetchImportHistory();
  }, []);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith(".json")) {
      toast.error("Please upload a .json file");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result;
        JSON.parse(content); // Validate JSON
        setJsonInput(content);
        setValidationResult(null);
        setImportResult(null);
        toast.success(`File loaded: ${file.name}`);
      } catch {
        toast.error("Invalid JSON file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // Parse JSON input
  const parseJsonInput = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return null;
    }
  };

  // Validate JSON
  const handleValidate = async () => {
    const passagesData = parseJsonInput();
    if (!passagesData) {
      toast.error("Invalid JSON format. Please check your input.");
      return;
    }
    
    setIsValidating(true);
    setValidationResult(null);
    setImportResult(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-import/validate`,
        {
          passages: passagesData,
          import_mode: importMode,
          default_status: defaultStatus,
          default_difficulty: parseInt(defaultDifficulty),
          default_exam_modes: ["general"],
          override_status: overrideStatus,
          override_difficulty: overrideDifficulty
        },
        { headers, withCredentials: true }
      );
      
      setValidationResult(response.data);
      
      if (response.data.valid) {
        toast.success(`Validation passed! ${response.data.valid_passages} passages ready to import.`);
      } else {
        toast.warning(`Found ${response.data.invalid_entries?.length || 0} invalid entries. Fix errors before importing.`);
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Failed to validate JSON");
    } finally {
      setIsValidating(false);
    }
  };

  // Import passages
  const handleImport = async () => {
    const passagesData = parseJsonInput();
    if (!passagesData) {
      toast.error("Invalid JSON format");
      return;
    }
    
    setIsImporting(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-import`,
        {
          passages: passagesData,
          import_mode: importMode,
          default_status: defaultStatus,
          default_difficulty: parseInt(defaultDifficulty),
          default_exam_modes: ["general"],
          override_status: overrideStatus,
          override_difficulty: overrideDifficulty
        },
        { headers, withCredentials: true }
      );
      
      setImportResult(response.data);
      setShowImportResultModal(true);
      
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported_passages} passages!`);
        onImportComplete?.();
        fetchImportHistory();
      } else {
        toast.error("Import failed. Check the results below.");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import passages");
    } finally {
      setIsImporting(false);
    }
  };

  // View imported passages (navigate to Passages tab with filter)
  const handleViewImported = () => {
    setShowImportResultModal(false);
    onNavigateToPassages?.(defaultStatus, parseInt(defaultDifficulty));
  };

  // Fetch sample template
  const fetchSampleTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/sample-template`, {
        headers,
        withCredentials: true
      });
      setSampleTemplate(response.data);
      setShowTemplate(true);
    } catch (error) {
      toast.error("Failed to load sample template");
    }
  };

  // Copy/Download/Use template
  const copyTemplate = () => {
    if (sampleTemplate?.template) {
      navigator.clipboard.writeText(JSON.stringify(sampleTemplate.template, null, 2));
      toast.success("Template copied to clipboard!");
    }
  };

  const useTemplate = () => {
    if (sampleTemplate?.template) {
      setJsonInput(JSON.stringify(sampleTemplate.template, null, 2));
      setShowTemplate(false);
      setValidationResult(null);
      setImportResult(null);
      toast.success("Template loaded into editor");
    }
  };

  const downloadTemplate = () => {
    if (sampleTemplate?.template) {
      const blob = new Blob([JSON.stringify(sampleTemplate.template, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rc_passages_template.json";
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Export functions
  const toggleExportSelection = (passageId) => {
    setSelectedForExport(prev => 
      prev.includes(passageId) 
        ? prev.filter(id => id !== passageId)
        : [...prev, passageId]
    );
  };

  const selectAllForExport = () => {
    if (selectedForExport.length === passages.length) {
      setSelectedForExport([]);
    } else {
      setSelectedForExport(passages.map(p => p.passage_id));
    }
  };

  const handleExport = async () => {
    if (selectedForExport.length === 0) {
      toast.error("Please select passages to export");
      return;
    }
    
    setIsExporting(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-export`,
        { passage_ids: selectedForExport },
        { headers, withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data.passages, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rc_passages_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} passages!`);
      setSelectedForExport([]);
    } catch (error) {
      toast.error("Failed to export passages");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    
    try {
      const response = await axios.get(`${API}/admin/bulk-export/all`, {
        headers,
        withCredentials: true
      });
      
      const blob = new Blob([JSON.stringify(response.data.passages, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rc_all_passages_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} passages!`);
    } catch (error) {
      toast.error("Failed to export passages");
    } finally {
      setIsExporting(false);
    }
  };

  const clearAll = () => {
    setJsonInput("");
    setValidationResult(null);
    setImportResult(null);
  };

  // Download error report
  const downloadErrorReport = () => {
    if (!importResult?.failed_entries?.length) return;
    
    const report = {
      import_id: importResult.import_id,
      timestamp: importResult.timestamp,
      total_in_json: importResult.total_in_json,
      success_count: importResult.imported_passages,
      failed_count: importResult.failed_count,
      failed_entries: importResult.failed_entries
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import_errors_${importResult.import_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDifficultyLabel = (level) => {
    const labels = { 1: "Beginner", 2: "Intermediate", 3: "Advanced", 4: "UPSC Editorial" };
    return labels[level] || `Level ${level}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-slate-100 text-slate-700",
      review: "bg-amber-100 text-amber-700",
      published: "bg-emerald-100 text-emerald-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-6">
      {/* Import Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Bulk Import Passages
              </CardTitle>
              <CardDescription>
                Import passages and questions from JSON file
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="gap-2"
              data-testid="toggle-history-btn"
            >
              <History className="w-4 h-4" />
              Import History
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Import History Panel */}
          {showHistory && (
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Imports
              </h4>
              {importHistory.length === 0 ? (
                <p className="text-sm text-slate-500">No import history yet.</p>
              ) : (
                <ScrollArea className="h-[150px]">
                  <div className="space-y-2">
                    {importHistory.map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                        <div>
                          <span className="font-mono text-xs text-slate-500">{record.import_id}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {record.success_count}/{record.total_in_json} imported
                            </Badge>
                            <Badge className={record.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                              {record.status}
                            </Badge>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(record.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* Import Settings Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column: Settings */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Import Settings
              </h4>
              
              {/* Import Mode */}
              <div className="space-y-2">
                <Label className="text-sm">Import Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger data-testid="import-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="continue_on_error">Continue on Error (import valid, skip invalid)</SelectItem>
                    <SelectItem value="all_or_nothing">All or Nothing (rollback on any error)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Settings */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Default Status</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="override-status"
                      checked={overrideStatus}
                      onCheckedChange={setOverrideStatus}
                      data-testid="override-status-checkbox"
                    />
                    <Label htmlFor="override-status" className="text-xs text-slate-600 cursor-pointer">
                      Override JSON status
                    </Label>
                  </div>
                </div>
                <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                  <SelectTrigger data-testid="default-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="review">In Review</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {overrideStatus 
                    ? "⚠️ All passages will use this status, ignoring JSON values"
                    : "Used only when JSON item has no 'status' field"}
                </p>
              </div>

              {/* Difficulty Settings */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Default Difficulty</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="override-difficulty"
                      checked={overrideDifficulty}
                      onCheckedChange={setOverrideDifficulty}
                      data-testid="override-difficulty-checkbox"
                    />
                    <Label htmlFor="override-difficulty" className="text-xs text-slate-600 cursor-pointer">
                      Override JSON difficulty
                    </Label>
                  </div>
                </div>
                <Select value={defaultDifficulty} onValueChange={setDefaultDifficulty}>
                  <SelectTrigger data-testid="default-difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Level 1 (Beginner)</SelectItem>
                    <SelectItem value="2">Level 2 (Intermediate)</SelectItem>
                    <SelectItem value="3">Level 3 (Advanced)</SelectItem>
                    <SelectItem value="4">Level 4 (UPSC Editorial)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {overrideDifficulty 
                    ? "⚠️ All passages will use this difficulty, ignoring JSON values"
                    : "Used only when JSON item has no 'difficulty' field"}
                </p>
              </div>
            </div>

            {/* Right Column: JSON Input */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm text-slate-700">JSON Input</h4>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchSampleTemplate}
                    className="gap-1"
                    data-testid="view-template-btn"
                  >
                    <HelpCircle className="w-3 h-3" />
                    Sample Template
                  </Button>
                </div>
              </div>
              
              {/* File Upload */}
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".json"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                  data-testid="upload-json-btn"
                >
                  <FileJson className="w-4 h-4" />
                  Upload JSON File
                </Button>
                {jsonInput && (
                  <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1">
                    <RefreshCw className="w-3 h-3" />
                    Clear
                  </Button>
                )}
              </div>

              {/* JSON Textarea */}
              <Textarea
                placeholder='Paste JSON array here: [{"title": "...", "content": "...", "questions": [...]}]'
                value={jsonInput}
                onChange={(e) => {
                  setJsonInput(e.target.value);
                  setValidationResult(null);
                  setImportResult(null);
                }}
                className="h-[200px] font-mono text-sm"
                data-testid="json-input-textarea"
              />

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={handleValidate} 
                  disabled={!jsonInput || isValidating}
                  className="flex-1 gap-2"
                  variant="outline"
                  data-testid="validate-json-btn"
                >
                  {isValidating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  Validate JSON
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={!jsonInput || isImporting || (validationResult && !validationResult.valid)}
                  className="flex-1 gap-2"
                  data-testid="import-btn"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Import Passages
                </Button>
              </div>
            </div>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className={`p-4 rounded-lg border ${
              validationResult.valid 
                ? "bg-emerald-50 border-emerald-200" 
                : "bg-amber-50 border-amber-200"
            }`}>
              <div className="flex items-center gap-2 mb-4">
                {validationResult.valid ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                <span className="font-semibold">
                  {validationResult.valid ? "✓ Validation Passed" : "⚠ Validation Issues Found"}
                </span>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-white/70 p-3 rounded text-center">
                  <p className="text-2xl font-bold">{validationResult.total_passages}</p>
                  <p className="text-xs text-slate-600">Total in JSON</p>
                </div>
                <div className="bg-white/70 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-emerald-600">{validationResult.valid_passages}</p>
                  <p className="text-xs text-slate-600">Valid Passages</p>
                </div>
                <div className="bg-white/70 p-3 rounded text-center">
                  <p className="text-2xl font-bold">{validationResult.total_questions}</p>
                  <p className="text-xs text-slate-600">Total Questions</p>
                </div>
                <div className="bg-white/70 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-red-600">{validationResult.invalid_entries?.length || 0}</p>
                  <p className="text-xs text-slate-600">Invalid Entries</p>
                </div>
              </div>

              {/* Status/Difficulty Summary */}
              {validationResult.valid && validationResult.status_summary && (
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-white/50 rounded">
                  <div>
                    <span className="text-xs text-slate-500 block mb-1">Status Distribution:</span>
                    <div className="flex gap-2">
                      {Object.entries(validationResult.status_summary).map(([status, count]) => (
                        count > 0 && (
                          <Badge key={status} className={getStatusColor(status)}>
                            {status}: {count}
                          </Badge>
                        )
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block mb-1">Difficulty Distribution:</span>
                    <div className="flex gap-2">
                      {Object.entries(validationResult.difficulty_summary).map(([level, count]) => (
                        count > 0 && (
                          <Badge key={level} variant="outline">
                            L{level}: {count}
                          </Badge>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {validationResult.preview?.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Preview ({validationResult.preview.length} passages):</p>
                  <ScrollArea className="h-[150px] border rounded bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">#</th>
                          <th className="text-left p-2">Title</th>
                          <th className="text-left p-2">Category</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">Difficulty</th>
                          <th className="text-left p-2">Questions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.preview.map((p, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 text-slate-400">{p.index + 1}</td>
                            <td className="p-2 truncate max-w-[200px]">{p.title}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">{p.category}</Badge>
                            </td>
                            <td className="p-2">
                              <Badge className={`text-xs ${getStatusColor(p.status)}`}>
                                {p.status}
                                {p.used_default_status && <span className="ml-1">*</span>}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">
                                L{p.difficulty}
                                {p.used_default_difficulty && <span className="ml-1">*</span>}
                              </Badge>
                            </td>
                            <td className="p-2">{p.question_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                  <p className="text-xs text-slate-500 mt-1">* = default value applied</p>
                </div>
              )}

              {/* Error List */}
              {validationResult.invalid_entries?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">
                    Errors ({validationResult.invalid_entries.length}):
                  </p>
                  <ScrollArea className="h-[120px] border rounded bg-white">
                    <div className="p-2 space-y-2">
                      {validationResult.invalid_entries.map((entry, i) => (
                        <div key={i} className="p-2 bg-red-50 rounded border border-red-100">
                          <p className="font-medium text-red-800 text-sm">
                            #{entry.index + 1}: {entry.title}
                          </p>
                          <ul className="list-disc list-inside text-red-700 text-xs mt-1">
                            {entry.errors.map((err, j) => (
                              <li key={j}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-600" />
            Export Passages
          </CardTitle>
          <CardDescription>
            Export passages and questions as JSON for backup or migration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Export Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={handleExportAll}
              disabled={isExporting || passages.length === 0}
              className="gap-2"
              data-testid="export-all-btn"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export All ({passages.length})
            </Button>
            <Button
              variant="outline"
              onClick={selectAllForExport}
              disabled={passages.length === 0}
              data-testid="select-all-export-btn"
            >
              {selectedForExport.length === passages.length ? "Deselect All" : "Select All"}
            </Button>
            {selectedForExport.length > 0 && (
              <Button 
                variant="secondary"
                onClick={handleExport}
                disabled={isExporting}
                className="gap-2"
                data-testid="export-selected-btn"
              >
                <Download className="w-4 h-4" />
                Export Selected ({selectedForExport.length})
              </Button>
            )}
          </div>

          {/* Passage Selection Grid */}
          <ScrollArea className="h-[300px] border rounded-lg">
            <div className="p-2 space-y-1">
              {passages.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No passages available to export</p>
                </div>
              ) : (
                passages.map((passage) => (
                  <div
                    key={passage.passage_id}
                    onClick={() => toggleExportSelection(passage.passage_id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${
                      selectedForExport.includes(passage.passage_id)
                        ? "bg-blue-100 border border-blue-300"
                        : "bg-white hover:bg-slate-50 border border-slate-200"
                    }`}
                    data-testid={`export-passage-${passage.passage_id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedForExport.includes(passage.passage_id)
                        ? "bg-blue-600 border-blue-600"
                        : "border-slate-300"
                    }`}>
                      {selectedForExport.includes(passage.passage_id) && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{passage.title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{passage.category}</Badge>
                        <Badge className={`text-xs ${getStatusColor(passage.status)}`}>{passage.status}</Badge>
                        <span className="text-xs text-slate-500">
                          L{passage.difficulty} • {passage.question_count}Q
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Sample Template Modal */}
      {showTemplate && sampleTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileJson className="w-5 h-5" />
                  Sample JSON Template
                </CardTitle>
                <CardDescription>
                  Use this template as a reference for bulk import
                </CardDescription>
              </div>
              <Button variant="ghost" onClick={() => setShowTemplate(false)}>✕</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Schema Docs */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Passage Fields</h4>
                  <div className="space-y-1 text-xs">
                    {Object.entries(sampleTemplate.schema_description?.passage_fields || {}).map(([key, desc]) => (
                      <p key={key}>
                        <code className="bg-slate-200 px-1 rounded">{key}</code>: {desc}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Question Fields</h4>
                  <div className="space-y-1 text-xs">
                    {Object.entries(sampleTemplate.schema_description?.question_fields || {}).map(([key, desc]) => (
                      <p key={key}>
                        <code className="bg-slate-200 px-1 rounded">{key}</code>: {desc}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Template Preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Sample Template</Label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copyTemplate} className="gap-1">
                      <ClipboardCopy className="w-3 h-3" />
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={downloadTemplate} className="gap-1">
                      <Download className="w-3 h-3" />
                      Download
                    </Button>
                    <Button size="sm" onClick={useTemplate} className="gap-1">
                      <FileJson className="w-3 h-3" />
                      Use Template
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <pre className="p-4 text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(sampleTemplate.template, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Result Modal */}
      {showImportResultModal && importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {importResult.success ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  Import {importResult.success ? "Complete" : "Failed"}
                </CardTitle>
                <CardDescription>
                  Import ID: {importResult.import_id}
                </CardDescription>
              </div>
              <Button variant="ghost" onClick={() => setShowImportResultModal(false)}>✕</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold">{importResult.total_in_json}</p>
                  <p className="text-xs text-slate-600">Total in JSON</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.imported_passages}</p>
                  <p className="text-xs text-slate-600">Imported</p>
                </div>
                <div className="bg-blue-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.imported_questions}</p>
                  <p className="text-xs text-slate-600">Questions</p>
                </div>
                <div className="bg-red-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-red-600">{importResult.failed_count}</p>
                  <p className="text-xs text-slate-600">Failed</p>
                </div>
              </div>

              {/* Rollback Notice */}
              {importResult.rollback_performed && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">⚠️ Rollback Performed</p>
                  <p className="text-red-700 text-sm">{importResult.rollback_reason}</p>
                </div>
              )}

              {/* Success Details */}
              {importResult.imported_details?.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-2">Imported Passages:</p>
                  <ScrollArea className="h-[150px] border rounded bg-slate-50">
                    <div className="p-2 space-y-1">
                      {importResult.imported_details.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                          <span className="truncate flex-1">{item.title}</span>
                          <div className="flex gap-2">
                            <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
                            <Badge variant="outline">L{item.difficulty}</Badge>
                            <Badge variant="outline">{item.questions}Q</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Failed Entries */}
              {importResult.failed_entries?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm text-red-700">Failed Entries:</p>
                    <Button size="sm" variant="outline" onClick={downloadErrorReport} className="gap-1">
                      <Download className="w-3 h-3" />
                      Download Error Report
                    </Button>
                  </div>
                  <ScrollArea className="h-[120px] border rounded bg-red-50">
                    <div className="p-2 space-y-1">
                      {importResult.failed_entries.map((item, idx) => (
                        <div key={idx} className="p-2 bg-white rounded border border-red-100 text-sm">
                          <p className="font-medium text-red-800">#{item.index + 1}: {item.title}</p>
                          <p className="text-red-600 text-xs">{item.error}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={handleViewImported}
                  className="flex-1 gap-2"
                  data-testid="view-imported-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Imported Passages
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowImportResultModal(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BulkImportExport;
